import React from 'react';
import { Box, TextField, Typography } from '@mui/material';

type ColorPickerProps = {
    label: string;
    value: string;
    onChange: (color: string) => void;
};

const ColorPicker: React.FC<ColorPickerProps> = ({ label, value, onChange }) => {
    const [hexValue, setHexValue] = React.useState<string>(value || '#000000');
    const prevValueRef = React.useRef<string>(value);

    React.useEffect(() => {
        // Only update if the value prop actually changed
        if (value !== prevValueRef.current) {
            setHexValue(value || '#000000');
            prevValueRef.current = value;
        }
    }, [value]);

    const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let hex = e.target.value.trim();
        
        // Allow empty input while typing
        if (hex === '') {
            setHexValue('');
            return;
        }
        
        // Add # if not present
        if (!hex.startsWith('#')) {
            hex = '#' + hex;
        }
        
        // Update local state to show what user typed
        setHexValue(hex);
        
        // Only call onChange when we have valid hex
        if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)) {
            onChange(hex);
        }
    };

    const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const color = e.target.value;
        // Only call onChange, don't update local state
        // The useEffect will sync when parent updates
        onChange(color);
    };

    return (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Typography fontSize={12} fontWeight={500} mb={0.5} sx={{ width: 180, maxWidth: '100%', flexShrink: 0 }}>{label}</Typography>
            <TextField
                placeholder="Enter hex color code"
                size="small"
                value={hexValue}
                onChange={handleHexChange}
                sx={{ width: 150, minWidth: 90, flex: '1 1 90px' }}
            />
            <TextField
                type="color"
                value={hexValue || '#000000'}
                onChange={handleColorChange}
                sx={{
                    flexShrink: 0,
                    '& input[type="color"]': {
                        height: 35,
                        width: 50,
                        cursor: 'pointer',
                        border: 'none',
                        padding: 0
                    },
                    '& fieldset': {
                        border: 'none',
                        padding: 0
                    }
                }}
            />
        </Box>
    );
};

export default ColorPicker;
